//"use client"

import { useEffect, useState } from "react"
import { buildPageTitle } from "../../utils/pageTitle"
import { useParams, useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet"
import "./CaseStudyDetail.css"
import { IoIosCall, IoMdArrowDropright } from "react-icons/io"
import axiosInstance from "../../api/axiosInstance"
import { getPreloadedData, PreloadedScript } from "../../utils/preloadedData"
import { ensureMetaDescription } from "../../utils/metaDescription"

// Helper function to create a URL-friendly slug from the title (matches the format used by CaseStudy.js links)
const createSlug = (title) => {
  if (!title) return ""
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
}

// Helper to build a safe, truncated meta description from plain text
const truncateForMeta = (text, maxLength = 160) => {
  if (!text) return ""
  const plain = text.replace(/\s+/g, " ").trim()
  return plain.length > maxLength ? plain.slice(0, maxLength).trim() + "..." : plain
}

function CaseStudyDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [caseStudy, setCaseStudy] = useState(() => getPreloadedData("__PRELOADED_CASE_STUDY__"))
  // Not loading when the prerendered snapshot already supplied the data, so the first client render matches it.
  const [isLoading, setIsLoading] = useState(() => getPreloadedData("__PRELOADED_CASE_STUDY__") === null)
  const [error, setError] = useState(null)
  const [isVisible, setIsVisible] = useState(false)
  const baseUrl = process.env.REACT_APP_BASE_URL

  useEffect(() => {
    const fetchCaseStudy = async () => {
      try {
        const response = await axiosInstance.get(`/api/case-studies/${id}`)
        if (!response.data) throw new Error("Case study not found")
        setCaseStudy(response.data)
        // Trigger animations after content loads
        setTimeout(() => setIsVisible(true), 100)
      } catch (error) {
        setError("Failed to load case study details. Please try again later.")
        console.error("Error fetching case study:", error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchCaseStudy()
  }, [id, baseUrl])

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in')
        }
      })
    }, observerOptions)

    const animatedElements = document.querySelectorAll('.animate-on-scroll')
    animatedElements.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [caseStudy])

  if (isLoading) {
    return (
      <div className="loader-container">
        <div className="loader">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="error-message">
        <div className="error-content animate-in">
          {error}
          <button className="btn-back-case-studies" onClick={() => navigate("/case-studies")}>
            <IoMdArrowDropright /> Go back to Case Studies
          </button>
        </div>
      </div>
    )
  }

  if (!caseStudy) return <div className="case-study-not-found">Case study not found.</div>

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return `${baseUrl}/images/default-image.jpg`
    return `${baseUrl}${imageUrl.startsWith("/") ? imageUrl : "/" + imageUrl}`
  }

  const pageTitle = buildPageTitle(caseStudy.title)
  const pageDescription = ensureMetaDescription(truncateForMeta(caseStudy.project_overview), caseStudy.title, "A 360 SynergyTech case study in AI, cloud, and software solutions.")
  const canonicalUrl = `https://360synergytech.com/case/study/detail/${id}/${createSlug(caseStudy.title)}`
  const ogImage = getImageUrl(caseStudy.image_url)

  return (
    <div className="case-study-detail-container">
      <PreloadedScript id="__PRELOADED_CASE_STUDY__" data={caseStudy} />
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://360synergytech.com/" },
              { "@type": "ListItem", position: 2, name: "Case Studies", item: "https://360synergytech.com/case-studies" },
              { "@type": "ListItem", position: 3, name: caseStudy.title, item: canonicalUrl },
            ],
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <div className={`hero-section ${isVisible ? 'hero-visible' : ''}`}>
        <img
          src={getImageUrl(caseStudy.image_url)}
          className="hero-image"
          alt={caseStudy.title}
          width="1200"
          height="500"
          onError={(e) => {
            e.target.onerror = null
            e.target.src = `${baseUrl}/images/default-image.jpg`
          }}
        />
        <div className="hero-overlay">
          <h1 className={`hero-title ${isVisible ? 'title-animate' : ''}`}>{caseStudy.title}</h1>
        </div>
      </div>

      {/* Content Wrapper */}
      <div className="case-study-content-wrapper">
        {/* Main Content Area */}
        <div className="main-content-area">
          <section className="section overview animate-on-scroll fade-in-up delay-1">
            <h2>Project Overview</h2>
            <p>{caseStudy.project_overview}</p>
          </section>

          <section className="section challenges animate-on-scroll fade-in-up delay-2">
            <h2>Challenges</h2>
            <p>{caseStudy.challenges}</p>
          </section>

          <section className="section solution animate-on-scroll fade-in-up delay-3">
            <h2>Our Solution</h2>
            <p>{caseStudy.our_solution}</p>
          </section>

          <section className="section results animate-on-scroll fade-in-up delay-4">
            <h2>Results</h2>
            <p>{caseStudy.results}</p>
          </section>

          <section className="section summary animate-on-scroll fade-in-up delay-5">
            <h2>Project Summary</h2>
            <p>{caseStudy.project_summary}</p>
          </section>
        </div>

        {/* Sidebar Area */}
        <div className="sidebar-area animate-on-scroll slide-in-right delay-6">
          <div className="project-info-card">
            <p className="project-info-heading">Project Info</p>
            <div className="info-rows">
              <div className="info-row">
                <p className="info-label">Category:</p>
                <p className="info-value">{caseStudy.category}</p>
              </div>
              <div className="info-row">
                <p className="info-label">Client:</p>
                <p className="info-value">{caseStudy.client}</p>
              </div>
              <div className="info-row">
                <p className="info-label">Location:</p>
                <p className="info-value">{caseStudy.location}</p>
              </div>
              <div className="info-row">
                <p className="info-label">Completed Date:</p>
                <p className="info-value">{caseStudy.completed_date}</p>
              </div>
              <div className="info-row">
                <p className="info-label">Project Value:</p>
                <p className="info-value">{caseStudy.project_value}</p>
              </div>
              <div className="info-row">
                <p className="info-label">Manner:</p>
                <p className="info-value">{caseStudy.manner}</p>
              </div>
              <div className="info-row">
                <p className="info-label">Designer:</p>
                <p className="info-value">{caseStudy.designer}</p>
              </div>
            </div>
          </div>

          <div className="call-to-action-card">
            <button className="call-button" aria-label="Call us">
              <IoIosCall />
            </button>
            <p className="call-heading">Have any Questions? Call us Today!</p>
            <p className="call-phone">+92 324 9925737</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CaseStudyDetail